<template>
  <div class="owner-approvals">
    <el-card class="section-card search-card" shadow="never">
      <div class="search-head">
        <div>
          <p class="eyebrow">Owner Actions</p>
          <h2>Approve employee</h2>
          <p class="subcopy">Review employee onboarding records that are not approved yet, download documents, and approve or deny the request.</p>
        </div>
        <el-input
          v-model.trim="search"
          class="search-input"
          clearable
          placeholder="Search by name or email"
        />
      </div>
    </el-card>

    <div v-if="loading" class="loading-grid">
      <el-skeleton
        v-for="n in 2"
        :key="n"
        animated
        :rows="5"
        class="section-card skeleton-card"
      />
    </div>

    <el-empty
      v-else-if="!filteredItems.length"
      description="No non-approved employee records are waiting right now."
      :image-size="90"
      class="section-card empty-card"
    />

    <div v-else class="cards-grid">
      <el-card
        v-for="item in filteredItems"
        :key="item.id"
        class="section-card approval-card"
        shadow="never"
      >
        <div class="approval-head">
          <div>
            <p class="case-id">Employee Review</p>
            <h3>{{ item.name || 'Unnamed employee' }}</h3>
            <p class="muted">{{ item.email || 'No email on file' }}</p>
          </div>
          <el-tag type="warning" round effect="light">
            {{ item.employeePortal?.status || 'pending_approval' }}
          </el-tag>
        </div>

        <div class="detail-grid">
          <div class="detail-item">
            <span class="detail-label">Verification submitted</span>
            <strong>{{ formatDate(item.employeePortal?.verificationSubmittedAt) || 'Not available' }}</strong>
          </div>
          <div class="detail-item">
            <span class="detail-label">Form completed</span>
            <strong>{{ formatDate(item.employeePortal?.formCompletedAt) || 'Not available' }}</strong>
          </div>
          <div class="detail-item">
            <span class="detail-label">Selfie</span>
            <strong>
              <el-link
                v-if="item.employeePortal?.selfieUrl"
                :href="item.employeePortal.selfieUrl"
                target="_blank"
                rel="noopener"
              >
                Download selfie
              </el-link>
              <span v-else>Not available</span>
            </strong>
          </div>
          <div class="detail-item">
            <span class="detail-label">Identification document</span>
            <strong>
              <el-link
                v-if="item.employeePortal?.idDocumentUrl"
                :href="item.employeePortal.idDocumentUrl"
                target="_blank"
                rel="noopener"
              >
                Download document
              </el-link>
              <span v-else>Not available</span>
            </strong>
          </div>
        </div>

        <div class="actions">
          <el-button
            type="success"
            :loading="actingId === item.id && actingAction === 'approve'"
            @click="approve(item)"
          >
            Approve
          </el-button>
          <el-button
            type="danger"
            plain
            :loading="actingId === item.id && actingAction === 'deny'"
            @click="deny(item)"
          >
            Deny
          </el-button>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  approveEmployeeByOwner,
  denyEmployeeByOwner,
  watchPendingEmployeeApprovals,
} from '@/services/owner'

export default {
  name: 'OwnerApprovalsPanel',
  props: {
    currentUser: {
      type: Object,
      default: null,
    },
    ownerProfile: {
      type: Object,
      default: null,
    },
  },
  emits: ['countChanged'],
  data() {
    return {
      loading: true,
      search: '',
      items: [],
      unsub: null,
      actingId: '',
      actingAction: '',
    }
  },
  computed: {
    filteredItems() {
      const term = this.search.trim().toLowerCase()
      if (!term) return this.items
      return this.items.filter((item) => {
        const haystack = [
          item.name,
          item.email,
          item.uid,
        ].filter(Boolean).join(' ').toLowerCase()
        return haystack.includes(term)
      })
    },
  },
  mounted() {
    this.subscribe()
  },
  beforeUnmount() {
    this.unsub && this.unsub()
  },
  methods: {
    subscribe() {
      this.unsub && this.unsub()
      this.loading = true
      this.unsub = watchPendingEmployeeApprovals((rows) => {
        this.items = rows
        this.loading = false
        this.$emit('countChanged', rows.length)
      })
    },
    formatDate(ts) {
      if (!ts) return ''
      try {
        const ms = ts.toMillis ? ts.toMillis() : (ts.seconds || 0) * 1000
        return new Date(ms).toLocaleString()
      } catch {
        return ''
      }
    },
    async approve(item) {
      this.actingId = item.id
      this.actingAction = 'approve'
      try {
        await approveEmployeeByOwner({
          userRecord: item,
          ownerUser: this.currentUser,
          ownerProfile: this.ownerProfile,
        })
        ElMessage({ type: 'success', message: `${item.email || item.name} was approved.`, showClose: true })
      } catch (error) {
        ElMessage({
          type: 'error',
          message: error?.message || 'Unable to approve this employee right now.',
          showClose: true,
          duration: 5000,
        })
      } finally {
        this.actingId = ''
        this.actingAction = ''
      }
    },
    async deny(item) {
      try {
        const { value } = await ElMessageBox.prompt(
          'Enter the reason the employee should fix before submitting verification again.',
          'Deny Employee',
          {
            inputType: 'textarea',
            inputPlaceholder: 'Write the denial comment',
            inputValidator: (text) => (!!String(text || '').trim()) || 'A denial comment is required.',
            confirmButtonText: 'Deny employee',
            cancelButtonText: 'Cancel',
          }
        )

        this.actingId = item.id
        this.actingAction = 'deny'
        await denyEmployeeByOwner({
          userRecord: item,
          comment: value,
          ownerUser: this.currentUser,
          ownerProfile: this.ownerProfile,
        })
        ElMessage({ type: 'success', message: `${item.email || item.name} was denied.`, showClose: true })
      } catch (error) {
        if (error === 'cancel' || error === 'close') return
        ElMessage({
          type: 'error',
          message: error?.message || 'Unable to deny this employee right now.',
          showClose: true,
          duration: 5000,
        })
      } finally {
        this.actingId = ''
        this.actingAction = ''
      }
    },
  },
}
</script>

<style scoped>
.owner-approvals {
  display: grid;
  gap: 16px;
}

.search-card,
.approval-card,
.empty-card,
.skeleton-card {
  border-radius: 22px;
}

.search-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.search-input {
  width: min(360px, 100%);
}

.eyebrow {
  margin: 0 0 6px;
  color: #0b6cff;
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

h2 {
  margin: 0 0 6px;
  color: #03241f;
  font-size: 1.5rem;
  font-weight: 850;
}

.subcopy,
.muted {
  margin: 0;
  color: #4f6d67;
}

.loading-grid,
.cards-grid {
  display: grid;
  gap: 16px;
}

.approval-card {
  background:
    radial-gradient(circle at top right, rgba(11, 108, 255, 0.08), transparent 32%),
    linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}

.approval-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 18px;
}

.case-id {
  margin: 0 0 6px;
  color: #0b6cff;
  font-size: 0.86rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.approval-head h3 {
  margin: 0 0 4px;
  color: #03241f;
  font-size: 1.25rem;
  font-weight: 850;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.detail-item {
  padding: 14px;
  border-radius: 16px;
  background: rgba(0, 56, 49, 0.04);
  border: 1px solid rgba(0, 56, 49, 0.08);
}

.detail-label {
  display: block;
  margin-bottom: 6px;
  color: #4f6d67;
  font-size: 0.84rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.detail-item strong {
  color: #03241f;
  font-weight: 750;
  white-space: normal;
  overflow-wrap: anywhere;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
}

@media (max-width: 860px) {
  .search-head {
    flex-direction: column;
  }

  .search-input {
    width: 100%;
  }
}

@media (max-width: 720px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }

  .approval-head {
    flex-direction: column;
  }
}
</style>
